import type { JSX } from 'react';
import {
  createTranslationRef,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';

/**
 * Messages of the app's own components (AD-10). pt-BR is the default
 * language, so these are the default messages; another language is a
 * translation resource for this ref, not a code change.
 */
export const byxTranslationRef = createTranslationRef({
  id: 'byx',
  messages: {
    nav: {
      label: 'Menu principal',
      workspace: 'Espaço de trabalho',
      admin: 'Admin',
      home: 'Início',
      catalog: 'Catálogo',
      apis: 'APIs',
      docs: 'Docs',
      create: 'Criar',
      settings: 'Configurações',
      visualizer: 'Visualizer',
      pageHeader: {
        trail: 'Trilha',
        sections: 'Seções da página',
      },
      topBar: {
        searchPlaceholder: 'Buscar serviços, APIs, infra, docs, pessoas…',
        searchLabel: 'Buscar no Backstage',
        create: 'Criar',
        notifications: 'Notificações',
        notificationsUnread: 'Notificações ({{count}} não lidas)',
        account: 'Configurações da conta',
      },
    },
    catalog: {
      tableTitle: '{{label}} ({{count}})',
      userFilter: {
        owned: 'Meus',
        starred: 'Favoritos',
        all: 'Todos',
      },
      tabs: {
        label: 'Tipo de entidade',
        component: 'Componentes',
        api: 'APIs',
        resource: 'Recursos',
        system: 'Sistemas',
        group: 'Squads',
        template: 'Templates',
        countLoading: '–',
      },
      views: {
        label: 'Visualização',
        list: 'Lista',
        graph: 'Grafo',
      },
      registerExisting: 'Registrar existente',
      columns: {
        name: 'Nome',
        owner: 'Dono',
        type: 'Tipo',
        lifecycle: 'Ciclo de vida',
        parent: 'Squad pai',
        items: 'Itens',
      },
      lifecycle: {
        production: 'produção',
        experimental: 'experimental',
        deprecated: 'descontinuado',
      },
      tags: {
        label: 'Tags',
        more: '+{{count}}',
        moreLabel: 'Mais {{count}} tags: {{tags}}',
      },
      loading: 'Carregando o catálogo…',
      error: {
        message: 'Não foi possível carregar o catálogo. Tente de novo.',
        retry: 'Tentar de novo',
      },
      empty: {
        filtered: 'Nada encontrado com esses filtros.',
        clearFilters: 'Limpar filtros',
        component: 'Nenhum componente no catálogo ainda.',
        api: 'Nenhuma API no catálogo ainda.',
        resource: 'Nenhum recurso no catálogo ainda.',
        system: 'Nenhum sistema no catálogo ainda.',
        group: 'Nenhuma squad no catálogo ainda.',
        template: 'Nenhum template no catálogo ainda.',
        other: 'Nada no catálogo ainda.',
      },
      exposure: {
        title: 'Exposição',
        public: 'Pública na internet',
        internal: 'Só interna',
        mixed: 'Mista',
        anonymous: 'Com rotas anônimas',
      },
    },
    home: {
      shortcutsLabel: 'Atalhos',
      shortcuts: {
        catalog: 'Catálogo',
        apis: 'APIs',
        docs: 'Docs',
        registerExisting: 'Registrar existente',
      },
      launch: {
        title: 'Subir nova aplicação',
        body: 'Preencha o formulário e o portal abre o PR da aplicação em dev, com ECR, roles IAM e secret. O repositório pode ser anexado depois.',
        create: 'Criar aplicação',
        templates: 'Ver templates',
      },
      visits: {
        recentTitle: 'Visitados recentemente',
        topTitle: 'Mais visitados',
        loading: 'Carregando…',
        error: 'Não foi possível carregar as visitas.',
        recentEmpty: 'Suas últimas páginas abertas vão aparecer aqui.',
        topEmpty: 'As páginas que você mais abre vão aparecer aqui.',
        hitsOne: '{{count}} acesso',
        hitsOther: '{{count}} acessos',
      },
      // Início › Execuções recentes; the columns, the error, the fallback
      // template name and the "Todas" link reuse the executions.* messages.
      executions: {
        title: 'Execuções recentes',
        loading: 'Carregando…',
        empty: 'As execuções dos templates que você rodar aparecem aqui.',
      },
      pages: {
        catalog: 'Catálogo',
        catalogGraph: 'Grafo',
        catalogImport: 'Registrar existente',
        apis: 'APIs',
        docs: 'Docs',
        create: 'Criar',
        templates: 'Templates',
        tasks: 'Execuções',
        search: 'Busca',
        notifications: 'Notificações',
        settings: 'Configurações',
      },
    },
    entity: {
      kinds: {
        component: 'Componente',
        api: 'API',
        resource: 'Recurso',
        system: 'Sistema',
        group: 'Squad',
        domain: 'Domínio',
        user: 'Pessoa',
        template: 'Template',
        location: 'Localização',
      },
      kindType: '{{kind}} · {{type}}',
      header: {
        back: 'Catálogo',
        favorite: 'Favoritar',
        unfavorite: 'Remover dos favoritos',
        repository: 'Repositório',
        repositoryLabel: 'Repositório (abre em nova aba)',
        graph: 'Ver no grafo',
        more: 'Mais ações',
        owner: 'Dono',
        system: 'Sistema',
        loading: 'Carregando a página…',
      },
      tabs: {
        label: 'Seções da página',
        componentLabel: 'Seções do componente',
        apiLabel: 'Seções da API',
        squadLabel: 'Seções da squad',
        systemLabel: 'Seções do sistema',
        groupLabel: 'Seções de {{group}}',
        overview: 'Visão geral',
        dependencies: 'Dependências',
        definition: 'Definição',
        documentation: 'Docs',
        deployment: 'Deploys',
        gateway: 'Gateway',
        infrastructure: 'Infraestrutura',
        api: 'API',
        development: 'Desenvolvimento',
        operation: 'Operação',
        observability: 'Observabilidade',
      },
      notFound: {
        component: 'Componente não encontrado',
        api: 'API não encontrada',
        resource: 'Recurso não encontrado',
        system: 'Sistema não encontrado',
        group: 'Squad não encontrada',
        domain: 'Domínio não encontrado',
        user: 'Pessoa não encontrada',
        other: 'Entidade não encontrada',
        error: 'Não foi possível carregar esta página.',
        back: 'Voltar ao Catálogo',
      },
      menu: {
        refresh: 'Atualizar entidade',
        refreshScheduled: 'Atualização agendada.',
        refreshFailed: 'Não foi possível agendar a atualização.',
      },
      card: {
        error: 'Não foi possível carregar {{card}}.',
        loading: 'Carregando {{card}}…',
      },
      page: {
        content: 'esta seção',
      },
      table: {
        none: '—',
      },
      panels: {
        retry: 'Tentar de novo',
      },
      about: {
        title: 'Sobre',
        owner: 'Dono',
        system: 'Sistema',
        domain: 'Domínio no gateway',
        repository: 'Repositório',
        repositoryLabel: '{{repo}} (abre em nova aba)',
        tags: 'Tags',
      },
      relations: {
        title: 'Relações',
        depth: 'Profundidade',
        depthOption: 'Profundidade {{depth}}',
        simplified: 'Simplificado',
        openGraph: 'Abrir no grafo',
        summary: 'Grafo de relações de {{name}}. {{details}}',
        owner: 'Dono: {{name}}.',
        system: 'Sistema: {{name}}.',
        count: '{{relation}}: {{count}}.',
        none: 'Sem outras relações.',
        types: {
          providesApi: 'Fornece',
          consumesApi: 'Consome',
          dependsOn: 'Depende de',
          dependencyOf: 'É dependência de',
          partOf: 'Parte de',
          hasPart: 'Contém',
          ownedBy: 'Dono',
          ownerOf: 'Dono de',
          apiProvidedBy: 'Fornecida por',
          apiConsumedBy: 'Consumida por',
          childOf: 'Filha de',
          parentOf: 'Pai de',
          memberOf: 'Membro de',
          hasMember: 'Membros',
        },
      },
      infra: {
        title: 'Infraestrutura',
        attach: 'Anexar existente',
        create: 'Criar recurso',
        empty: 'Nenhuma infraestrutura anexada a este componente.',
        columns: {
          type: 'Tipo',
          name: 'Nome',
          detail: 'Detalhe',
        },
      },
      environments: {
        title: 'Ambientes',
        caption: 'Argo CD',
        synced: 'Sincronizado',
        outOfSync: 'Fora de sincronia',
        notDeployed: 'Não implantado',
        promote: 'Promover',
        promoteLabel: 'Promover para {{env}}',
        revisionLabel: 'Revisão {{sha}}',
      },
      routes: {
        title: 'Rotas no gateway',
        exposure: 'Exposição: {{value}}',
        internal: 'Interna',
        public: 'Internet',
        authentication: 'Autenticação: {{value}}',
        authenticated: 'Autenticada',
        anonymous: 'Anônima',
        viewAll: 'Ver todas',
        viewAllLabel: 'Ver todas as rotas no gateway',
      },
      gateway: {
        title: 'Gateway',
        domain: 'Domínio',
        envLabel: 'Rotas de {{env}}',
        columns: {
          method: 'Método',
          path: 'Caminho',
          exposure: 'Exposição',
          authentication: 'Autenticação',
          scopes: 'Escopos',
        },
        moreScopes: '+{{count}}',
        moreScopesLabel: '+{{count}}. Escopos: {{scopes}}',
        host: 'Host',
        hostInternal: 'Host interno',
        hostPublic: 'Host público',
        hostSeparator: ' · ',
        hostLabel: '{{label}}: ',
        hostTitle: '{{label}}: {{host}}',
      },
      apiTab: {
        title: 'APIs fornecidas',
      },
      api: {
        exposure: 'Exposição',
        environments: 'Ambientes publicados',
        providedBy: 'Fornecida por',
        consumedBy: 'Consumida por',
        noConsumers: 'Nenhum componente declara que consome esta API.',
      },
      definition: {
        title: 'Definição',
        raw: 'Ver bruto',
        rawLabel: 'Definição bruta',
        error: 'Não foi possível exibir a definição.',
        format: '{{name}} {{version}}',
      },
      squad: {
        items: 'Itens da squad',
        components: 'Componentes',
        apis: 'APIs',
        resources: 'Recursos',
        systems: 'Sistemas',
        viewInCatalog: 'Ver no catálogo',
        viewComponents: 'Ver no catálogo: componentes da squad',
        viewApis: 'Ver no catálogo: APIs da squad',
        viewResources: 'Ver no catálogo: recursos da squad',
        viewSystems: 'Ver no catálogo: sistemas da squad',
        parent: 'Squad pai',
        children: 'Squads filhas',
        members: 'Membros',
        contact: 'Contato',
        empty: 'Esta squad ainda não é dona de nenhum item no catálogo.',
        error: 'Não foi possível carregar os itens da squad.',
      },
      system: {
        parts: 'Partes do sistema',
        domain: 'Domínio',
        empty: 'Nenhuma parte registrada para este sistema.',
        error: 'Não foi possível carregar as partes do sistema.',
      },
      links: {
        title: 'Links',
        newTab: '{{title}} (abre em nova aba)',
        argocd: 'Argo CD',
      },
      dependencies: {
        title: 'Dependências',
        provides: 'Fornece (APIs)',
        consumes: 'Consome (APIs)',
        dependsOn: 'Depende de',
      },
    },
    templateCard: {
      goldenPath: '★ Caminho dourado',
      tags: 'Tags',
      steps: 'Passos',
      showSteps: 'Ver passos',
      hideSteps: 'Ocultar passos',
      use: 'Usar',
    },
    docs: {
      columns: {
        document: 'Documento',
        owner: 'Dono',
        type: 'Tipo',
      },
      kindTypeSeparator: ' · ',
      loading: 'Carregando a lista de documentos…',
      error: 'Não foi possível carregar a lista de documentos.',
      empty: {
        none: 'Nenhuma documentação publicada ainda. Para publicar, adicione a anotação {{annotation}} ao {{file}} do seu serviço.',
        publishGuide: 'Como publicar',
        publishGuideLabel: 'Como publicar (abre em nova aba)',
      },
    },
    shared: {
      squadChip: 'Squad: {{name}}',
    },
    // Grafo do catálogo (Stories 5.5, 2.3): the app's own texts. The filter
    // titles and options are the stock plugin's, translated in
    // catalogGraph/translations.ts.
    catalogGraph: {
      backToList: 'Voltar à lista',
      picker: {
        label: 'Item central do grafo',
        placeholder: 'Escolha um item para ver as relações',
        searching: 'Buscando…',
        noResults: 'Nenhum item encontrado.',
        error: 'Não foi possível buscar os itens.',
      },
      filters: {
        label: 'Filtros do grafo',
        more: '+{{count}}',
      },
      hint: 'Role para aproximar e arraste para mover. Clique num item para centralizar; clique duplo para abrir a página.',
      empty:
        'Escolha um item no campo acima para ver com quem ele se relaciona.',
      loading: 'Montando o grafo…',
      // The stock spinner while the graph reloads after a re-centre (U6-6).
      busy: 'Carregando o grafo',
      error: 'Não foi possível montar o grafo.',
      graphLabel: 'Grafo de relações de {{name}}',
    },
    catalogImport: {
      url: {
        label: 'URL do repositório ou do arquivo',
        required: ' *',
        hint: 'Endereço completo do {{file}} ou do repositório no GitHub',
        placeholder: 'https://github.com/byxcapital/…',
      },
      analyzing: 'Analisando…',
      error:
        'Não foi possível analisar esta URL. Confira o endereço e se o arquivo ou o repositório existe.',
      help: {
        fileExample:
          'https://github.com/byxcapital/meu-servico/blob/main/catalog-info.yaml',
        repositoryExample: 'https://github.com/byxcapital/meu-servico',
        learnMoreLabel: '{{title}} (abre em nova aba)',
        // Stands in for app.title when the config has none.
        appTitle: 'portal',
      },
      // Steps 2–4 (UI spec v2, U5-8): the texts the stock steps hard-code
      // in English, and the pull request the wizard prepares.
      locations: {
        entities: 'Entidades: {{count}}',
        expand: 'Mostrar as entidades de {{target}}',
        collapse: 'Ocultar as entidades de {{target}}',
      },
      reviewError:
        'Não foi possível registrar as entidades. Tente de novo ou fale com o time de Plataforma.',
      pullRequest: {
        codeowners: 'Usar o arquivo {{file}} como dono da entidade',
        title: 'Adicionar o arquivo {{file}}',
        body: 'Este pull request adiciona um **arquivo de descrição de entidades** a este repositório, para que o componente entre no [catálogo do {{appTitle}}]({{appUrl}}).\n\nDepois que o pull request for aceito, o componente aparece no catálogo.',
      },
    },
    settings: {
      identity: {
        noSquads: 'Nenhuma squad',
      },
      providers: {
        empty: 'Nenhum provedor de login disponível.',
      },
    },
    // Página não encontrada and Sem permissão (U5-9, AC-30): every stock
    // not-found use, and the Registrar existente permission guard.
    errorPages: {
      notFound: {
        title: 'Página não encontrada',
        text: 'O endereço pode ter mudado ou a página não existe mais.',
        home: 'Voltar ao Início',
        back: 'Voltar',
      },
      noPermission: {
        registerTitle: 'Sem permissão para registrar',
        registerText:
          'Sua conta não pode registrar componentes no catálogo. Fale com o time de Plataforma se precisar desse acesso.',
        catalog: 'Voltar ao Catálogo',
      },
      checkError: {
        text: 'Não foi possível verificar seu acesso.',
        retry: 'Tentar de novo',
      },
    },
    // Times in the portal language, not the browser's (Q-31, AC-32): the
    // locale of every date and relative time — the app's helpers and, through
    // luxon's default locale, the stock components — and the app's own word
    // for "just now". Another language brings its own.
    time: {
      dateLocale: 'pt-BR',
      now: 'agora',
    },
    search: {
      // Locale of the numbers on the page ("1.204"), part of the messages so
      // another language brings its own separators.
      numberLocale: 'pt-BR',
      results: {
        one: '{{count}} resultado para “{{query}}”',
        other: '{{count}} resultados para “{{query}}”',
      },
      filters: {
        resultType: 'Tipo de resultado',
        all: 'Todos',
        catalog: 'Catálogo',
        docs: 'Documentação',
        kind: 'Tipo de entidade',
        kindAll: 'Todos',
        lifecycle: 'Ciclo de vida',
      },
      listLabel: 'Resultados da busca',
      docsKind: 'Documentação',
      owner: 'Dono: {{name}}',
      pagination: {
        label: 'Paginação dos resultados',
        pageSize: 'Resultados por página',
        range: '{{from}}–{{to}}',
        previous: 'Página anterior',
        next: 'Próxima página',
      },
      empty: {
        noQuery:
          'Digite na busca acima para encontrar serviços, APIs, docs e pessoas.',
        noResults: 'Nada encontrado para “{{query}}”.',
        clearFilters: 'Limpar filtros',
      },
      loading: 'Carregando os resultados…',
      error: 'A busca não respondeu. Tente de novo.',
    },
    executions: {
      none: '—',
      masked: '••••',
      retry: 'Tentar de novo',
      status: {
        open: 'Na fila',
        processing: 'Em andamento',
        completed: 'Concluída',
        failed: 'Falhou',
        cancelled: 'Cancelada',
        skipped: 'Ignorada',
        notStarted: 'Não iniciado',
        interrupted: 'Interrompida',
      },
      duration: {
        seconds: '{{seconds}} s',
        minutes: '{{minutes}} min {{seconds}} s',
        hours: '{{hours}} h {{minutes}} min',
      },
      list: {
        title: 'Execuções ({{count}})',
        countLoading: '–',
        ownership: {
          label: 'Quem iniciou',
          owned: 'Minhas',
          all: 'Todas',
        },
        filter: {
          placeholder: 'Filtrar nesta página',
          clear: 'Limpar filtro',
          clearText: 'Limpar texto do filtro',
          countOne: '{{count}} execução nesta página',
          countOther: '{{count}} execuções nesta página',
        },
        columns: {
          template: 'Template',
          target: 'Alvo',
          status: 'Status',
          startedBy: 'Iniciada por',
          when: 'Quando',
        },
        when: '{{relative}} ({{absolute}})',
        empty: {
          owned: 'Você ainda não executou nenhum template.',
          viewTemplates: 'Ver templates',
          all: 'Nenhuma execução ainda.',
          filter: 'Nenhuma execução nesta página corresponde ao filtro.',
        },
        loading: 'Carregando as execuções…',
        error: 'Não foi possível carregar as execuções.',
        pagination: {
          label: 'Paginação das execuções',
          range: '{{from}}–{{to}} de {{total}}',
          previous: 'Página anterior',
          next: 'Próxima página',
        },
      },
      detail: {
        breadcrumb: 'Execução',
        loading: 'Carregando a execução…',
        notFound: 'Execução não encontrada',
        viewExecutions: 'Ver execuções',
        error: 'Não foi possível carregar a execução.',
        startedBy: 'Iniciada por {{name}}',
        startedAt: '{{day}} {{month}}, {{time}}',
        inputs: 'entradas:',
        inputsUnavailable: 'não disponíveis',
        banner: {
          completed: 'Concluída em {{duration}}',
          failedAt: 'Falhou em {{step}}',
          processing: 'Em andamento…',
        },
        actions: {
          rerun: 'Executar de novo',
          cancel: 'Cancelar',
          cancelling: 'Cancelando…',
        },
        cancelError: 'Não foi possível cancelar a execução.',
        tabs: {
          label: 'Visualização da execução',
          logs: 'Status e logs',
          output: 'Saída',
        },
        steps: {
          label: 'Etapas',
          rowLabel: '{{name}}, {{status}}, {{duration}}',
          rowLabelNoDuration: '{{name}}, {{status}}',
        },
        logs: {
          allToggle: 'Todos os logs ({{count}})',
          allTitle: 'Todos os logs',
          search: 'Buscar nos logs',
          copy: 'Copiar logs',
          copied: 'Logs copiados',
          matches: '{{count}} de {{total}} linhas',
          regionLabel: 'Logs de {{step}}',
          allRegionLabel: 'Logs de todas as etapas',
          empty: 'Este passo não registrou logs.',
          allEmpty: 'Nenhuma etapa registrou logs.',
          noMatch: 'Nenhuma linha corresponde à busca.',
          statusDuration: '{{status}} · {{duration}}',
        },
        output: {
          title: 'Saída',
          empty: 'Esta execução não produziu saídas.',
          openInCatalog: 'Abrir no catálogo',
          link: 'Link',
          newTab: '{{title}} (abre em nova aba)',
        },
        live: {
          step: 'Etapa {{name}}: {{status}}',
          execution: 'Execução: {{status}}',
        },
      },
    },
  },
});

/** A key of {@link byxTranslationRef}. */
export type ByxMessageKey = keyof typeof byxTranslationRef.T;

/**
 * Translation function of {@link byxTranslationRef}. Declared by hand: the
 * full inferred type of `t` for this many messages is too large for the
 * compiler to export. Keys stay checked; values for `{{…}}` are strings.
 */
export type ByxT = (
  key: ByxMessageKey,
  options?: Record<string, string>,
) => string;

/** The app's messages, read in components with `const { t } = useByxT()`. */
export function useByxT(): { t: ByxT } {
  const { t } = useTranslationRef(byxTranslationRef);
  return { t: t as ByxT };
}

/**
 * Translation function for a message with React elements as values (a code
 * chip, an emphasised word inside a sentence): the message stays one string
 * for translators and the result is an element.
 */
export type ByxRichT = (
  key: ByxMessageKey,
  options: Record<string, string | JSX.Element>,
) => JSX.Element;

/** The app's messages with element values: `const { t } = useByxRichT()`. */
export function useByxRichT(): { t: ByxRichT } {
  const { t } = useTranslationRef(byxTranslationRef);
  return { t: t as unknown as ByxRichT };
}
